import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Activity, PlayCircle, LayoutGrid } from "lucide-react";
import { FurtherReading } from "@/components/FurtherReading";
import { useCaseList } from "@/context/ContentContext";
import { VitalsMonitor } from "@/components/visuals/VitalsMonitor";
import { serveCase, pickRandom } from "./beatUtils";
import { renderBeat, TensionMeter } from "./beats";
import { Debrief } from "./Debrief";

export default function BeatEngine({ config }) {
  const cfg = config;
  const cases = useCaseList(cfg.simId, cfg.cases);
  const [stage, setStage] = useState("intro");
  const [caseData, setCaseData] = useState(null);
  const [beatIdx, setBeatIdx] = useState(0);
  const [scores, setScores] = useState([]);
  const [debrief, setDebrief] = useState(null);
  const [tension, setTension] = useState(45);

  const accent = cfg.accent || "#0047AB";
  const Icon = cfg.Icon || Activity;

  const begin = (excludeId) => {
    setCaseData(serveCase(pickRandom(cases, excludeId)));
    setBeatIdx(0);
    setScores([]);
    setDebrief(null);
    setTension(45);
    setStage("running");
  };

  const onBeatDone = (scoreObj) => {
    const next = [...scores, scoreObj];
    if (beatIdx < caseData.beats.length - 1) {
      setScores(next);
      setBeatIdx(beatIdx + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const earned = next.reduce((a, b) => a + b.earned, 0);
    const max = next.reduce((a, b) => a + b.max, 0) || 1;
    setDebrief({ percent: Math.round((earned / max) * 100), breakdown: next, caseTitle: caseData.name });
    setStage("debrief");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const totalBeats = caseData?.beats.length || cfg.steps.length;
  const currentBeat = caseData?.beats[beatIdx];

  return (
    <div className="min-h-screen bg-[#F4F7F9]">
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-3 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="engine-home-link">
            <div className="w-8 h-8 rounded-md flex items-center justify-center text-white" style={{ background: accent }}>
              <Icon className="w-4.5 h-4.5" />
            </div>
            <div>
              <div className="text-sm font-semibold tracking-tight text-slate-900">{cfg.simName}</div>
              <div className="text-[11px] text-slate-500 -mt-0.5">{cfg.subtitle}</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            {stage === "running" && (
              <div className="flex gap-1.5" data-testid="engine-progress">
                {Array.from({ length: totalBeats }).map((_, i) => (
                  <span key={i} className="w-5 h-1.5 rounded-full" style={{ background: i <= beatIdx ? accent : "#e2e8f0" }} />
                ))}
              </div>
            )}
            <Link
              to="/sections"
              data-testid="engine-sections-btn"
              className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-slate-200 bg-white text-xs font-semibold text-slate-600 hover:text-slate-900 hover:border-slate-300 transition-colors whitespace-nowrap"
            >
              <LayoutGrid className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Simulator sections</span>
              <span className="sm:hidden">Sections</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        {stage === "intro" && (
          <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-sm fade-up text-center" data-testid="engine-intro">
            <div className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto text-white" style={{ background: accent }}>
              <Icon className="w-7 h-7" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-4">{cfg.title}</h1>
            <p className="text-base text-slate-600 mt-3 leading-relaxed max-w-2xl mx-auto">{cfg.introBlurb}</p>
            <div className="flex flex-wrap items-center justify-center gap-2 mt-5">
              {cfg.steps.map((s) => (
                <span key={s} className="text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded bg-slate-100 text-slate-600">{s}</span>
              ))}
            </div>
            <div className="mt-7">
              <Button onClick={() => begin(null)} data-testid="start-drill-btn" className="text-white text-base px-6 py-5" style={{ background: accent }}>
                <PlayCircle className="w-5 h-5 mr-2" /> Start a case · {cases.length} scenario{cases.length === 1 ? "" : "s"}
              </Button>
            </div>
            <FurtherReading topic={cfg.topic} className="mt-8 text-left" />
          </div>
        )}

        {stage === "running" && caseData && currentBeat && (
          <div data-testid="engine-running">
            {caseData.monitor && <VitalsMonitor vitals={caseData.monitor} className="mb-5" />}
            {cfg.conversation && <TensionMeter value={tension} />}
            {renderBeat(
              { ...currentBeat, last: beatIdx === totalBeats - 1 },
              {
                beatKey: `${caseData.id}-${beatIdx}`,
                eyebrow: `Stage ${beatIdx + 1} of ${totalBeats} · ${cfg.steps[beatIdx] || ""}`,
                showBriefing: true,
                c: caseData,
                accent,
                conversation: !!cfg.conversation,
                timeLimit: cfg.timed ? (cfg.timeLimit || 25) : undefined,
                onTensionDelta: (d) => setTension((t) => Math.max(0, Math.min(100, t + d))),
                onDone: onBeatDone,
              }
            )}
          </div>
        )}

        {stage === "debrief" && debrief && (
          <Debrief cfg={cfg} debrief={debrief} accent={accent} onNext={() => begin(caseData.id)} onRestart={() => setStage("intro")} />
        )}
      </main>
    </div>
  );
}
